import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { QuoteDetail, WorkItem } from '@techgitter/contracts';
import { getWorkItemByNumber } from '../../services/workItemDataService';
import { linkTicket } from '../../services/quotesApi';

interface LinkTicketDialogProps {
  open: boolean;
  onClose: () => void;
  quote: QuoteDetail;
  onLinked: () => void;
}

const DetailRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
);

export const LinkTicketDialog: React.FC<LinkTicketDialogProps> = ({
  open,
  onClose,
  quote,
  onLinked,
}) => {
  const [ticketNumber, setTicketNumber] = useState('');
  const [ticket, setTicket] = useState<WorkItem | null>(null);
  const [looking, setLooking] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTicketNumber('');
    setTicket(null);
    setError(null);
  };

  const handleClose = () => {
    if (looking || linking) return;
    reset();
    onClose();
  };

  const handleLookup = async () => {
    const num = ticketNumber.trim();
    if (!num || !/^\d+$/.test(num)) {
      setError('Enter a numeric ticket number.');
      return;
    }
    setLooking(true);
    setError(null);
    setTicket(null);
    try {
      const found = await getWorkItemByNumber(Number(num));
      if (!found) {
        setError(
          `Ticket ${num} wasn’t found. Check the number — it must already exist and be synced to SM4RT.`,
        );
        return;
      }
      setTicket(found);
    } catch {
      setError(
        `Ticket ${num} wasn’t found. Check the number — it must already exist and be synced to SM4RT.`,
      );
    } finally {
      setLooking(false);
    }
  };

  const handleConfirm = async () => {
    if (!ticket || ticket.number == null) return;
    setLinking(true);
    setError(null);
    try {
      await linkTicket(quote.number, String(ticket.number));
      onLinked();
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLinking(false);
    }
  };

  const customerMismatch =
    ticket &&
    quote.customerId &&
    ticket.customerId &&
    ticket.customerId !== quote.customerId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link an existing ticket</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          Link this quote to a RepairShopr ticket raised outside the quote flow.
          No line items are added — the quote is simply bound to the ticket and
          stays editable.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Ticket number"
            value={ticketNumber}
            onChange={(e) => {
              setTicketNumber(e.target.value);
              setTicket(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLookup();
            }}
            disabled={looking || linking}
            size="small"
            fullWidth
            autoFocus
          />
          <Button
            variant="outlined"
            onClick={handleLookup}
            disabled={!ticketNumber.trim() || looking || linking}
          >
            {looking ? <CircularProgress size={16} /> : 'Look up'}
          </Button>
        </Box>

        {ticket && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              mb: 1,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Ticket {ticket.number}
              </Typography>
              {ticket.status && (
                <Chip size="small" label={ticket.status} />
              )}
            </Stack>
            {ticket.title && (
              <Typography variant="body2" color="text.secondary">
                {ticket.title}
              </Typography>
            )}
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.5}>
              <DetailRow
                label="Customer"
                value={
                  ticket.customerBusinessName || ticket.customerName || '—'
                }
              />
            </Stack>
            {customerMismatch && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                This ticket’s customer differs from the quote’s customer —
                double-check it’s the right ticket before linking.
              </Alert>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={looking || linking}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!ticket || looking || linking}
        >
          {linking ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Linking…
            </>
          ) : (
            'Link ticket'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkTicketDialog;
